// imports
import { useState, useEffect, useMemo } from 'react';

// local imports
import { useGetAwdConfigQuery, selectSectors } from '../../../../services/awdConfigApi';
import { initialWaterDataState } from '../../../../store/waterDataSlice';
import ReusableMenu from '../ReusableMenu';
import { getUrlParam } from '../../../../utils/getUrlParam';

// Define prop types
interface SectorMenuProps {
  setSelectedSectorLocal: (sector: string) => void;
}

export default function SectorMenu({
  setSelectedSectorLocal,
}: SectorMenuProps) {
  const { data, isLoading } = useGetAwdConfigQuery();

  const sectorOptions = useMemo(() => {
    if (!data) return [];
    return selectSectors(data.models);
  }, [data]);

  // Initialize selectedIndex from URL params - gives URL precedence over defaults
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Initialize local state once sector options are available
  useEffect(() => {
    if (sectorOptions.length === 0) return;

    const sectorFromUrl = getUrlParam('sector');

    if (sectorFromUrl) {
      const foundIndex = sectorOptions.findIndex(
        (option) => option.key === sectorFromUrl
      );

      if (foundIndex !== -1) {
        // Syncing selectedIndex with the URL parameter on mount so the UI reflects
        // a bookmarked or shared URL. This runs once on mount (empty dep array)
        // and does not create a reactive loop.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedIndex(foundIndex);
        setSelectedSectorLocal(sectorFromUrl);
      } else {
        setSelectedSectorLocal(sectorOptions[0].key);
      }
    } else {
      // No URL param — use Redux default, fallback to first option
      const defaultSector = initialWaterDataState.selectedSector;
      const defaultIndex = sectorOptions.findIndex((opt) => opt.key === defaultSector);
      if (defaultIndex !== -1) {
        setSelectedIndex(defaultIndex);
        setSelectedSectorLocal(defaultSector);
      } else {
        setSelectedSectorLocal(sectorOptions[0].key);
      }
    }
  }, [sectorOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || sectorOptions.length === 0) return null;

  const cloudfrontPrefix = import.meta.env.DEV ? `${import.meta.env.VITE_NWDC_S3_URL}` : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;
  const learnMoreLink = `${cloudfrontPrefix}/data-catalog/sector/${sectorOptions[selectedIndex].key}`;

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    const selectedSector = sectorOptions[index].key;

    // Update local state only - URL params updated when Update Map is clicked
    setSelectedSectorLocal(selectedSector);
  };

  return (
    <ReusableMenu
      title="Sector"
      options={sectorOptions}
      selectedIndex={selectedIndex}
      onSelect={handleSelect}
      learnMoreLink={learnMoreLink}
    />
  );
}
